<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="520">
        <v-card rounded="xl">
            <!-- Header -->
            <v-card-title class="d-flex justify-space-between align-center pa-4 pb-0">
                <h4 class="text-h6 font-weight-bold">Register Device</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="close"/>
            </v-card-title>

            <v-card-text class="pa-5">
                <!-- Success Screen -->
                <div v-if="showSuccess" class="text-center py-8">
                    <v-icon size="72" color="success" class="mb-4">mdi-check-circle</v-icon>
                    <h3 class="text-h5 font-weight-bold mb-2">All Set!</h3>
                    <p class="text-body-1 text-grey-darken-1 mb-6">Your tracker has been registered and configured.</p>
                    <div class="d-flex justify-center ga-3">
                        <v-btn variant="tonal" @click="registerAnother">
                            <v-icon start>mdi-plus</v-icon>
                            Register Another
                        </v-btn>
                        <v-btn color="primary" @click="goToLiveView">
                            <v-icon start>mdi-map-marker</v-icon>
                            Go to Live View
                        </v-btn>
                    </div>
                </div>

                <!-- Wizard Steps -->
                <template v-else>
                    <wizard-step-indicator :steps="stepLabels" :current-step="displayStep"/>

                    <v-divider class="mb-5"/>

                    <!--
                        Payment Step. OWNER ONLY.

                        Registering a device is open to admins (TRACKER_PROVISION
                        covers ADMIN and OWNER) but adding a card is not - every
                        payments.* route sits in PersonalRoleTier's BILLING set,
                        granted to OWNER alone. So a promoted admin who reached
                        this step was shown a real Stripe card element, typed in
                        a real card number, and got "Something went wrong while
                        adding your payment method. Please try again." - a
                        permanent refusal worded as a transient failure, after
                        committing to a multi-step wizard.
                    -->
                    <step-payment-method
                        v-if="currentView === 'payment' && authStore.isCircleOwner"
                        :key="paymentKey"
                        @payment-added="onPaymentAdded"
                    />

                    <!-- The same step for someone who cannot complete it. -->
                    <div v-else-if="currentView === 'payment'" class="text-center py-6">
                        <v-icon size="40" color="medium-emphasis" class="mb-3">mdi-credit-card-off-outline</v-icon>
                        <div class="text-body-1 mb-2">A payment method is needed first</div>
                        <div class="text-medium-emphasis mb-4">
                            Only the circle owner can add a card. Ask them to add one,
                            then register this tracker again.
                        </div>
                        <v-btn variant="text" @click="close">Close</v-btn>
                    </div>

                    <!-- Activate Step -->
                    <step-activate-device
                        v-else-if="currentView === 'activate'"
                        :key="activateKey"
                        @activated="onDeviceActivated"
                        @payment-required="onPaymentRequired"
                    />

                    <!-- Setup Tracker Step -->
                    <step-setup-tracker
                        v-else-if="currentView === 'setup' && activatedDeviceId"
                        :device-id="activatedDeviceId"
                        :nickname="activatedNickname"
                        @completed="onSetupCompleted"
                    />
                </template>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, computed, watch} from "vue";
import {useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import WizardStepIndicator from "@/components/onboarding/WizardStepIndicator.vue";
import StepPaymentMethod from "@/components/onboarding/StepPaymentMethod.vue";
import {useAuthStore} from "@/stores/authStore";
import StepActivateDevice from "@/components/onboarding/StepActivateDevice.vue";
import StepSetupTracker from "@/components/onboarding/StepSetupTracker.vue";

const props = defineProps({
    modelValue: Boolean,
});
const emit = defineEmits(['update:modelValue', 'addDevice']);

const router = useRouter();
const dialogSwitch = ref(props.modelValue);
const authStore = useAuthStore();
const currentView = ref('activate');
const showSuccess = ref(false);
const activatedDeviceId = ref(null);
const activatedNickname = ref('');
const paymentKey = ref(0);
const activateKey = ref(0);
const needsPayment = ref(false);

const stepLabels = computed(() => {
    if (needsPayment.value) return ['Payment', 'Activate', 'Setup'];
    return ['Activate', 'Setup'];
});

const displayStep = computed(() => {
    if (needsPayment.value) {
        if (currentView.value === 'payment') return 1;
        if (currentView.value === 'activate') return 2;
        return 3;
    }
    if (currentView.value === 'activate') return 1;
    return 2;
});

watch(() => props.modelValue, (val) => {
    dialogSwitch.value = val;
    if (val) checkPaymentMethods();
});

watch(dialogSwitch, (val) => {
    if (!val) emit('update:modelValue', false);
});

const checkPaymentMethods = async () => {
    try {
        const response = await send('get', 'payments/methods');
        const data = response.data?.data;
        const methods = Array.isArray(data) ? data : (data?.payment_methods || []);
        if (methods.length > 0) {
            needsPayment.value = false;
            currentView.value = 'activate';
        } else {
            needsPayment.value = true;
            currentView.value = 'payment';
        }
    } catch {
        needsPayment.value = false;
        currentView.value = 'activate';
    }
};

const onPaymentAdded = () => {
    currentView.value = 'activate';
};

const onPaymentRequired = () => {
    needsPayment.value = true;
    currentView.value = 'payment';
};

const onDeviceActivated = ({device, nickname}) => {
    activatedDeviceId.value = device.id;
    activatedNickname.value = nickname;
    currentView.value = 'setup';
};

const onSetupCompleted = () => {
    showSuccess.value = true;
    emit('addDevice');
};

const registerAnother = async () => {
    showSuccess.value = false;
    activatedDeviceId.value = null;
    activatedNickname.value = '';
    paymentKey.value++;
    activateKey.value++;
    await checkPaymentMethods();
};

const goToLiveView = () => {
    close();
    router.push('/live-view');
};

const close = () => {
    emit('update:modelValue', false);
};

if (props.modelValue) {
    checkPaymentMethods();
}
</script>
